import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { studentAPI } from '../services/api';

function TakeTest() {
    const location = useLocation();
    const navigate = useNavigate();
    const { test, questions, studentName } = location.state || {};
    const [answers, setAnswers] = useState({});
    const [timeLeft, setTimeLeft] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(0);

    useEffect(() => {
        if (!test || !questions || !studentName) {
            navigate('/student');
            return;
        }

        // Timer starten wenn Zeitlimit gesetzt
        if (test.time_limit) {
            setTimeLeft(test.time_limit * 60);
        }
    }, [test, questions, studentName, navigate]);

    useEffect(() => {
        if (timeLeft === null || timeLeft <= 0) return;

        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    handleSubmit();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [timeLeft]);

    const handleAnswerChange = (questionId, value) => {
        setAnswers({
            ...answers,
            [questionId]: value
        });
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const formattedAnswers = Object.entries(answers).map(([questionId, answerData]) => ({
                question_id: parseInt(questionId),
                answer_data: answerData
            }));

            const response = await studentAPI.submitTest(test.id, studentName, formattedAnswers);
            navigate('/test-submitted', { 
                state: { 
                    submission: response.data,
                    testTitle: test.title
                } 
            });
        } catch (err) {
            console.error('Submit error:', err);
            setError(err.response?.data?.details || err.response?.data?.error || 'Fehler beim Abgeben des Tests');
            setLoading(false);
        }
    };

    if (!test) {
        return null;
    }

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const isWarningTime = timeLeft !== null && timeLeft < 300;

    const isYouTubeUrl = (url) => {
        return url && (url.includes('youtube.com') || url.includes('youtu.be'));
    };

    const getYouTubeEmbedUrl = (url) => {
        let videoId = '';
        if (url.includes('youtube.com/watch?v=')) {
            videoId = url.split('v=')[1]?.split('&')[0];
        } else if (url.includes('youtu.be/')) {
            videoId = url.split('youtu.be/')[1]?.split('?')[0];
        }
        return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    };

    const isImageUrl = (url) => {
        return url && /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(url);
    };

    const goToNextPage = () => {
        if (currentPage < questions.length - 1) {
            setCurrentPage(currentPage + 1);
            window.scrollTo(0, 0);
        }
    };

    const goToPreviousPage = () => {
        if (currentPage > 0) {
            setCurrentPage(currentPage - 1);
            window.scrollTo(0, 0);
        }
    };

    const isMultiPage = test.display_mode === 'multi_page';
    const displayQuestions = isMultiPage ? [questions[currentPage]] : questions;
    const startIndex = isMultiPage ? currentPage : 0;

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div>
                    <h1>{test.title}</h1>
                    <p style={styles.studentName}>Schüler: {studentName}</p>
                    {isMultiPage && (
                        <p style={styles.pageInfo}>
                            Frage {currentPage + 1} von {questions.length}
                        </p>
                    )}
                </div>
                {timeLeft !== null && (
                    <div style={{
                        ...styles.timer,
                        ...(isWarningTime ? styles.timerWarning : {})
                    }}>
                        ⏱️ {formatTime(timeLeft)}
                    </div>
                )}
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <form onSubmit={handleSubmit}>
                {displayQuestions.map((question, index) => {
                    const displayIndex = startIndex + index;
                    return (
                        <div key={question.id} style={styles.questionCard}>
                            <div style={styles.questionHeader}>
                                <h3>Frage {displayIndex + 1}</h3>
                                {question.points && question.points > 0 && (
                                    <span style={styles.points}>{question.points} Punkt(e)</span>
                                )}
                            </div>

                            <p style={styles.questionText}>{question.question_text}</p>

                            {(question.attachment_filename || question.attachment_url) && (
                                <div style={styles.attachment}>
                                    {question.attachment_filename && question.attachment_mimetype?.startsWith('image/') && (
                                        <img 
                                            src={`/api/tests/attachment/${question.id}`}
                                            alt={question.attachment_filename}
                                            style={styles.attachmentImage}
                                        />
                                    )}
                                    {question.attachment_filename && !question.attachment_mimetype?.startsWith('image/') && (
                                        <a 
                                            href={`/api/tests/attachment/${question.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={styles.attachmentLink}
                                        >
                                            📎 {question.attachment_filename}
                                        </a>
                                    )}

                                    {question.attachment_url && isYouTubeUrl(question.attachment_url) && getYouTubeEmbedUrl(question.attachment_url) && (
                                        <div style={styles.videoContainer}>
                                            <iframe
                                                width="100%"
                                                height="400"
                                                src={getYouTubeEmbedUrl(question.attachment_url)}
                                                frameBorder="0"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                                style={styles.video}
                                                title={`Video ${displayIndex + 1}`}
                                            ></iframe>
                                        </div>
                                    )}

                                    {question.attachment_url && !isYouTubeUrl(question.attachment_url) && isImageUrl(question.attachment_url) && (
                                        <img 
                                            src={question.attachment_url}
                                            alt="Anhang"
                                            style={styles.attachmentImage}
                                        />
                                    )}

                                    {question.attachment_url && !isYouTubeUrl(question.attachment_url) && !isImageUrl(question.attachment_url) && (
                                        <a 
                                            href={question.attachment_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={styles.attachmentLink}
                                        >
                                            🔗 {question.attachment_url}
                                        </a>
                                    )}
                                </div>
                            )}

                            {question.question_type === 'multiple_choice' && (
                                <div style={styles.options}>
                                    {JSON.parse(question.options).map((option, i) => (
                                        <label key={i} style={styles.optionLabel}>
                                            <input
                                                type="radio"
                                                name={`question_${question.id}`}
                                                value={option}
                                                checked={answers[question.id] === option}
                                                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                                                style={styles.radio}
                                            />
                                            {option}
                                        </label>
                                    ))}
                                </div>
                            )}

                            {question.question_type === 'true_false' && (
                                <div style={styles.options}>
                                    <label style={styles.optionLabel}>
                                        <input
                                            type="radio"
                                            name={`question_${question.id}`}
                                            value="true"
                                            checked={answers[question.id] === true}
                                            onChange={() => handleAnswerChange(question.id, true)}
                                            style={styles.radio}
                                        />
                                        Wahr
                                    </label>
                                    <label style={styles.optionLabel}>
                                        <input
                                            type="radio"
                                            name={`question_${question.id}`}
                                            value="false"
                                            checked={answers[question.id] === false}
                                            onChange={() => handleAnswerChange(question.id, false)}
                                            style={styles.radio}
                                        />
                                        Falsch
                                    </label>
                                </div>
                            )}

                            {question.question_type === 'free_text' && (
                                <textarea
                                    value={answers[question.id] || ''}
                                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                                    style={styles.textarea}
                                    rows="5"
                                    placeholder="Deine Antwort..."
                                />
                            )}
                        </div>
                    );
                })}

                {isMultiPage && (
                    <div style={styles.navigationSection}>
                        <button 
                            type="button"
                            onClick={goToPreviousPage}
                            disabled={currentPage === 0}
                            style={{
                                ...styles.navButton,
                                ...(currentPage === 0 ? styles.navButtonDisabled : {})
                            }}
                        >
                            ← Zurück
                        </button>
                        
                        <div style={styles.progressIndicator}>
                            {questions.map((_, index) => (
                                <div
                                    key={index}
                                    style={{
                                        ...styles.progressDot,
                                        ...(index === currentPage ? styles.progressDotActive : {}),
                                        ...(answers[questions[index].id] ? styles.progressDotAnswered : {})
                                    }}
                                    onClick={() => setCurrentPage(index)}
                                    title={`Frage ${index + 1}${answers[questions[index].id] ? ' (beantwortet)' : ''}`}
                                />
                            ))}
                        </div>

                        {currentPage < questions.length - 1 ? (
                            <button 
                                type="button"
                                onClick={goToNextPage}
                                style={styles.navButton}
                            >
                                Weiter →
                            </button>
                        ) : (
                            <button 
                                type="submit"
                                disabled={loading}
                                style={styles.submitButtonNav}
                            >
                                {loading ? 'Wird abgegeben...' : 'Test abgeben'}
                            </button>
                        )}
                    </div>
                )}

                {!isMultiPage && (
                    <div style={styles.submitSection}>
                        <p style={styles.submitHint}>
                            Beantwortet: {Object.keys(answers).length} von {questions.length} Fragen
                        </p>
                        <button 
                            type="submit" 
                            disabled={loading}
                            style={styles.submitButton}
                        >
                            {loading ? 'Wird abgegeben...' : 'Test abgeben'}
                        </button>
                    </div>
                )}
            </form>
        </div>
    );
}

const styles = {
    container: {
        padding: '2rem',
        maxWidth: '800px',
        margin: '0 auto',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
        paddingBottom: '1rem',
        borderBottom: '2px solid #eee',
        flexWrap: 'wrap',
        gap: '1rem',
    },
    studentName: {
        color: '#666',
        marginTop: '0.5rem',
    },
    pageInfo: {
        color: '#007bff',
        fontWeight: 'bold',
        marginTop: '0.5rem',
    },
    timer: {
        fontSize: '1.5rem',
        fontWeight: 'bold',
        padding: '1rem',
        backgroundColor: '#d4edda',
        borderRadius: '8px',
        color: '#155724',
    },
    timerWarning: {
        backgroundColor: '#f8d7da',
        color: '#721c24',
        animation: 'pulse 1s infinite',
    },
    questionCard: {
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '1.5rem',
    },
    questionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem',
    },
    points: {
        color: '#666',
        fontSize: '0.9rem',
    },
    questionText: {
        fontSize: '1.1rem',
        marginBottom: '1rem',
        lineHeight: '1.6',
    },
    attachment: {
        marginBottom: '1rem',
        padding: '1rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px',
    },
    attachmentImage: {
        maxWidth: '100%',
        height: 'auto',
        borderRadius: '4px',
        border: '1px solid #ddd',
        display: 'block',
    },
    attachmentLink: {
        display: 'inline-block',
        padding: '0.5rem 1rem',
        backgroundColor: '#007bff',
        color: 'white',
        textDecoration: 'none',
        borderRadius: '4px',
    },
    videoContainer: {
        position: 'relative',
        width: '100%',
        paddingBottom: '56.25%',
        height: 0,
        overflow: 'hidden',
    },
    video: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        borderRadius: '4px',
    },
    options: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
    },
    optionLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.75rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px',
        cursor: 'pointer',
        border: '2px solid transparent',
        transition: 'all 0.2s',
    },
    radio: {
        width: '20px',
        height: '20px',
        cursor: 'pointer',
    },
    textarea: {
        width: '100%',
        padding: '0.75rem',
        border: '1px solid #ddd',
        borderRadius: '4px',
        fontSize: '1rem',
        fontFamily: 'inherit',
        resize: 'vertical',
        boxSizing: 'border-box',
    },
    error: {
        backgroundColor: '#fee',
        color: '#c33',
        padding: '1rem',
        borderRadius: '4px',
        marginBottom: '1rem',
    },
    navigationSection: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '1rem',
        marginTop: '2rem',
        padding: '1.5rem',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        flexWrap: 'wrap',
    },
    navButton: {
        padding: '0.75rem 1.5rem',
        backgroundColor: '#007bff',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        fontSize: '1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
    },
    navButtonDisabled: {
        backgroundColor: '#ccc',
        cursor: 'not-allowed',
    },
    submitButtonNav: {
        padding: '0.75rem 1.5rem',
        backgroundColor: '#28a745',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        fontSize: '1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
    },
    progressIndicator: {
        display: 'flex',
        gap: '0.5rem',
        flexWrap: 'wrap',
        justifyContent: 'center',
        flex: 1,
    },
    progressDot: {
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        backgroundColor: '#dee2e6',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    progressDotActive: {
        backgroundColor: '#007bff',
        transform: 'scale(1.3)',
    },
    progressDotAnswered: {
        backgroundColor: '#28a745',
    },
    submitSection: {
        textAlign: 'center',
        marginTop: '2rem',
        padding: '2rem',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
    submitHint: {
        marginBottom: '1rem',
        color: '#666',
    },
    submitButton: {
        padding: '1rem 3rem',
        backgroundColor: '#28a745',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        fontSize: '1.1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
    },
};

export default TakeTest;
